import React, { useContext, useEffect } from 'react';
import { Journey, PageAbsolutePath } from '../../../consts/Common';
import Confirm from '../../common/Confirmation/Confirm';
import RenewalsQuoteCard from './quotes/RenewalsQuoteCard';
import { PaymentMethod } from '../../common/Constants';
import {
  getBenefits,
  getNoOfInstallments,
  getSelectedVariant,
  getProductInstalmentTotalPriceByPaymentMethod,
  getVariantInstalmentTotalPrice,
  calculateTotals
} from '../../common/ProductMethods';
import { ProductType } from '../../common/CommonTypes';
import { Force24Helper } from '../../common/Force24Helper';
import { SiteContext } from '../../../context/SiteContext';
import PageHeader from '../../common/PageHeader';
import { useJourney } from '../Layout';
import { useTranslation } from 'react-i18next';
import ConfirmationExtraDetails from '../../common/Confirmation/ConfirmationExtraDetails';

const RenewalsConfirmation = (): JSX.Element => {
  const { journey, onPageChange } = useJourney();
  const { products, personalDetail, orderNumbers } = journey;
  const { site } = useContext(SiteContext);
  const { t } = useTranslation();

  const cms = site.content;
  const emailConfirmationText = cms.confirmationPage.emailConfirmation.replace(
    '{EMAILADDRESS}',
    personalDetail.email
  );
  const referenceText = t('confirmation.your-reference', {
    orderNumbervalues: orderNumbers.join(' & '),
    interpolation: { escapeValue: false }
  });

  const noOfInstallments = getNoOfInstallments(products);
  const totals = calculateTotals(
    products.filter((q) => q.isSelected === true),
    noOfInstallments
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    onPageChange(PageAbsolutePath.Confirmation, true);

    if (site.brand.brandName.toLowerCase() === 'autotrust') {
      Force24Helper.setTracker();
      Force24Helper.updateTracker(totals?.selectedTotalPrice);
    }
  }, []);

  const renderQuotes = () => {
    return products
      .filter((q) => q.isSelected === true)
      .map((quote: ProductType) => {
        const selectedVariant = getSelectedVariant(quote.productVariants);

        if (selectedVariant) {
          const selectedLinkedProducts = quote.selectedLinkedProducts?.filter(
            (slp) => slp.quoteCardId === quote.productId
          );

          let subscriptionPrice = undefined;
          let singlePrice = undefined;
          let monthlyPrice = undefined;
          let totalPrice = undefined;

          if (selectedVariant.paymentMethod == PaymentMethod.Monthly) {
            const installmentPrice = getVariantInstalmentTotalPrice(
              quote.productId,
              selectedVariant,
              noOfInstallments,
              selectedLinkedProducts ?? [],
              quote.isVariablePrice
            );

            monthlyPrice = installmentPrice;

            totalPrice =
              installmentPrice.firstInstallmentPrice +
              installmentPrice.subsequentInstallmentPrice *
                (noOfInstallments - 1);
          } else if (
            selectedVariant.paymentMethod == PaymentMethod.Subscription
          ) {
            subscriptionPrice = getProductInstalmentTotalPriceByPaymentMethod(
              quote,
              PaymentMethod.Subscription,
              noOfInstallments,
              selectedLinkedProducts || []
            );
          } else {
            singlePrice = getProductInstalmentTotalPriceByPaymentMethod(
              quote,
              PaymentMethod.Single,
              noOfInstallments,
              selectedLinkedProducts || []
            );
          }

          return (
            <RenewalsQuoteCard
              key={`quote-${quote.productTypeId}-${quote.renewalGroupId}`}
              id={quote.renewalGroupId}
              productId={quote.productId}
              keyBenefitLists={getBenefits(
                cms.quotePage.productTypeCustomisations,
                quote,
                selectedLinkedProducts
              )}
              quoteTitle={selectedVariant.customerFriendlyName}
              quoteType={quote.productTypeId}
              priceMonthly={monthlyPrice}
              priceTotal={totalPrice}
              priceSingle={singlePrice?.firstInstallmentPrice}
              priceSubscription={subscriptionPrice?.firstInstallmentPrice}
              selectedOptionalLinkedQuotes={selectedLinkedProducts}
              linkedQuotes={selectedVariant.linkedProducts}
              isPaymentDone={true}
              showMoreInfo={true}
              showSelectOption={false}
              noOfInstallments={noOfInstallments}
              selectedQuote={quote.productId}
              productVariants={quote.productVariants}
              showAllPricingOptions={false}
              isVariablePrice={quote.isVariablePrice}
              culture={site.cultureCode}
            />
          );
        }
      });
  };

  return (
    <>
      <PageHeader
        title={cms.confirmationPage.title}
        description={cms.confirmationPage.description}
        information={cms.confirmationPage.information}
        warning={cms.confirmationPage.warning}
        descriptionClass='p2'
      />
      <Confirm
        subTitle={cms.confirmationPage.summaryDescription}
        emailConfirmationText={emailConfirmationText}
        orderNumbers={orderNumbers}
        referenceText={referenceText}>
        {site.journeyType === Journey.Renewals ? (
          renderQuotes()
        ) : (
          <ConfirmationExtraDetails referenceText={referenceText} />
        )}
      </Confirm>
    </>
  );
};

export default RenewalsConfirmation;
